import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Newspaper, Search, ExternalLink, RefreshCw, Loader2, Globe, Clock, X, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `há ${mins} min`;
  const h = Math.round(mins / 60);
  if (h < 24) return `há ${h} h`;
  return `há ${Math.round(h / 24)} d`;
}

function NewsCard({ item, featured }) {
  const [imgOk, setImgOk] = useState(!!item.image);
  const [sharing, setSharing] = useState(false);
  useEffect(() => { setImgOk(!!item.image); }, [item.image]);
  const shareToFeed = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (sharing) return;
    setSharing(true);
    try {
      await axios.post(`${API}/posts`, { content: item.title, link_preview: { url: item.link, title: item.title, image: item.image || null, source: item.source, favicon: item.favicon, description: item.description || "" } });
      toast.success("Notícia compartilhada no seu feed!", { action: { label: "Ver feed", onClick: () => (window.location.href = "/feed") } });
    } catch (err) { toast.error(err.response?.data?.detail || "Não foi possível compartilhar"); }
    setSharing(false);
  };
  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`news-card-${item.id}`}
      className={`group block rounded-2xl border bg-card overflow-hidden hover:border-primary/60 hover:shadow-lg transition-all ${featured ? "md:col-span-2" : ""}`}
    >
      <div className={`relative bg-muted overflow-hidden ${featured ? "aspect-[21/9]" : "aspect-[16/9]"}`}>
        {imgOk ? (
          <img src={item.image} alt="" loading="lazy" onError={() => setImgOk(false)} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" data-testid={`news-image-${item.id}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-600 text-white/70">
            <Newspaper className="w-10 h-10" />
          </div>
        )}
        <div className="absolute left-3 top-3 flex items-center gap-1.5 bg-black/60 backdrop-blur text-white text-[11px] font-semibold rounded-full pl-1 pr-2.5 py-0.5" data-testid={`news-source-${item.id}`}>
          {item.favicon && <img src={item.favicon} alt="" className="w-4 h-4 rounded-full bg-white" />}
          <span className="truncate max-w-[160px]">{item.source || "Fonte"}</span>
        </div>
      </div>
      <div className="p-4">
        <h3 className={`font-bold leading-snug group-hover:text-primary transition-colors ${featured ? "text-lg md:text-xl" : "text-sm md:text-base"} line-clamp-3`} data-testid={`news-title-${item.id}`}>{item.title}</h3>
        {item.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{item.description}</p>}
        <div className="flex items-center justify-between mt-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {timeAgo(item.published_at)}</span>
          <span className="flex items-center gap-2">
            <button type="button" onClick={shareToFeed} disabled={sharing} data-testid={`news-share-${item.id}`} className="flex items-center gap-1 font-semibold hover:text-primary transition-colors" title="Compartilhar no feed">
              <Share2 className="w-3 h-3" /> {sharing ? "..." : "Compartilhar"}
            </button>
            <span className="flex items-center gap-1 text-primary font-semibold">Ler matéria <ExternalLink className="w-3 h-3" /></span>
          </span>
        </div>
      </div>
    </a>
  );
}

export default function News() {
  const [cats, setCats] = useState([]);
  const [langs, setLangs] = useState([]);
  const [category, setCategory] = useState("top");
  const [lang, setLang] = useState("pt");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const reqId = useRef(0);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/news/categories`).then(r => { setCats(r.data.categories || []); setLangs(r.data.languages || []); }).catch(() => {});
    return () => pollRef.current && clearTimeout(pollRef.current);
  }, []);

  const load = async (silent = false, attempt = 0) => {
    const id = ++reqId.current;
    if (pollRef.current) clearTimeout(pollRef.current);
    if (!silent) { setLoading(true); setError(""); }
    try {
      const r = await axios.get(`${API}/news`, { params: { category, lang, q: query || undefined, limit: 30 } });
      if (id !== reqId.current) return;
      setItems(r.data.items || []);
      setUpdatedAt(new Date());
      if (r.data.enriching && attempt < 8) pollRef.current = setTimeout(() => load(true, attempt + 1), 2500);
    } catch (e) {
      if (id !== reqId.current) return;
      if (!silent) { setItems([]); setError(e.response?.data?.detail || "Não foi possível carregar as notícias agora."); }
    }
    if (id === reqId.current && !silent) setLoading(false);
  };

  useEffect(() => { load(); }, [category, lang, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitSearch = (e) => { e?.preventDefault(); setQuery(q.trim()); };
  const clearSearch = () => { setQ(""); setQuery(""); };

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="news-page">
      <Card className="p-5 relative overflow-hidden bg-slate-900 text-white border-slate-800">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-primary/30 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-wrap items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/40 shrink-0">
            <Newspaper className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Notícias do mundo</h1>
            <p className="text-xs md:text-sm text-white/70">Manchetes em tempo real de fontes globais · atualizado a cada 10 minutos</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white/10 rounded-full p-1" data-testid="news-lang-switch">
              {langs.map(l => (
                <button key={l.key} data-testid={`news-lang-${l.key}`} onClick={() => setLang(l.key)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${lang === l.key ? "bg-white text-slate-900" : "text-white/80 hover:bg-white/10"}`}>
                  {l.key === "pt" ? "PT-BR" : l.key === "en" ? "Global" : "ES"}
                </button>
              ))}
            </div>
            <Button size="icon" variant="ghost" className="rounded-full text-white hover:bg-white/10" onClick={() => load()} data-testid="news-refresh" aria-label="Atualizar">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        <form onSubmit={submitSearch} className="relative mt-4" data-testid="news-search-form">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar notícias por palavra-chave..." data-testid="news-search-input"
            className="pl-9 pr-24 rounded-full bg-white text-slate-900 border-0" />
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {query && <Button type="button" size="icon" variant="ghost" className="rounded-full w-8 h-8 text-slate-500" onClick={clearSearch} data-testid="news-search-clear"><X className="w-4 h-4" /></Button>}
            <Button type="submit" size="sm" className="rounded-full h-8" data-testid="news-search-submit">Buscar</Button>
          </div>
        </form>
      </Card>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="news-categories">
        {cats.map(c => (
          <button key={c.key} data-testid={`news-cat-${c.key}`} onClick={() => { setCategory(c.key); }}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${category === c.key && !query ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
        <span data-testid="news-status">
          {query ? <>Resultados para <b className="text-foreground">"{query}"</b></> : <>Categoria: <b className="text-foreground">{cats.find(c => c.key === category)?.label || "Principais"}</b></>}
          {items.length > 0 && ` · ${items.length} matérias`}
        </span>
        {updatedAt && <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> Atualizado {updatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>}
      </div>

      <SponsoredAdWidget variant="banner" limit={1} />

      {loading && items.length === 0 && (
        <div className="flex items-center justify-center py-20 text-muted-foreground gap-2" data-testid="news-loading"><Loader2 className="w-5 h-5 animate-spin" /> Carregando manchetes...</div>
      )}
      {error && !loading && (
        <Card className="p-8 text-center text-muted-foreground" data-testid="news-error">
          {error}
          <div className="mt-3"><Button variant="outline" size="sm" onClick={() => load()}>Tentar novamente</Button></div>
        </Card>
      )}
      {!loading && !error && items.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground" data-testid="news-empty">Nenhuma notícia encontrada.</Card>
      )}
      {items.length > 0 && (
        <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${loading ? "opacity-60" : ""}`} data-testid="news-grid">
          {(() => { const fi = items.findIndex(i => i.image); const ordered = fi > 0 ? [items[fi], ...items.filter((_, k) => k !== fi)] : items; return ordered.map((it, i) => <NewsCard key={it.id} item={it} featured={i === 0} />); })()}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground text-center pb-4">Fonte: Google News RSS · Os links abrem a matéria original no site do veículo.</p>
    </div>
  );
}
